/**
 * Conversational lead capture — name + email only.
 * Project details come from the prior chat transcript.
 */

const EMAIL_FIND_RE = /[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}/;
const EMAIL_FULL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const CANCEL_RE =
  /^(cancel|stop|never\s*mind|nevermind|quit|exit|back)$/i;
const AFFIRM_RE =
  /^(yes|y|yeah|yep|yup|sure|ok|okay|please|of course|sounds good|why not)[.!]?$/i;
const NAME_BLOCKLIST_RE =
  /^(yes|y|yeah|yep|yup|sure|ok|okay|no|nope|hi|hello|hey|thanks|thank you|please|skip|cancel)$/i;

export const START_LEAD_RE =
  /\b(leave (my )?(project )?details|share (my )?(contact )?details|contact (me|hiren)|get in touch|hire (him|hiren)|i('?m| am) interested|connect (me )?with hiren|let'?s connect|call me|email me|reach (out|me)|submit (my )?project|send (hiren )?my details)\b/i;

export const EMPTY_LEAD = {
  name: "",
  email: "",
};

const PROMPTS = {
  name: "Happy to connect you with **Hiren**. What’s your **full name**?",
  email: "Thanks{name}. What’s the best **email** to reach you?",
};

export function isCancel(text) {
  return CANCEL_RE.test(String(text || "").trim());
}

/** “Yes” after “want to keep chatting?” — not a lead answer. */
export function isAffirmation(text) {
  return AFFIRM_RE.test(String(text || "").trim());
}

export function shouldStartLead(text) {
  const value = String(text || "").trim();
  if (isAffirmation(value) || isCancel(value)) return false;
  return START_LEAD_RE.test(value);
}

export function extractEmail(text) {
  const m = String(text || "").match(EMAIL_FIND_RE);
  return m ? m[0] : "";
}

function extractName(text) {
  const raw = String(text || "").trim();
  if (!raw) return "";
  if (EMAIL_FULL_RE.test(raw)) return "";
  if (NAME_BLOCKLIST_RE.test(raw)) return "";
  const named = raw.match(
    /(?:my name is|i am|i'm|this is)\s+([A-Za-z][A-Za-z .'-]{1,60})/i
  );
  if (named) return named[1].replace(/[.,!]+$/, "").trim();
  const withoutEmail = raw.replace(EMAIL_FIND_RE, "").trim();
  if (NAME_BLOCKLIST_RE.test(withoutEmail)) return "";
  if (
    withoutEmail &&
    withoutEmail.length <= 60 &&
    !/\d{5,}/.test(withoutEmail) &&
    withoutEmail.split(/\s+/).length <= 5
  ) {
    return withoutEmail.replace(/[.,!]+$/, "").trim();
  }
  return "";
}

export function isValidEmail(email) {
  return EMAIL_FULL_RE.test(String(email || "").trim());
}

export function nextMissingStep(lead) {
  if (!String(lead.name || "").trim()) return "name";
  if (!isValidEmail(lead.email)) return "email";
  return null;
}

export function promptForStep(step, lead) {
  const base = PROMPTS[step] || "Please continue.";
  if (step === "email" && lead.name) {
    return base.replace("{name}", `, **${lead.name}**`);
  }
  return base.replace("{name}", "");
}

export function absorbReply(lead, step, text) {
  const value = String(text || "").trim();
  const next = { ...lead };
  const email = extractEmail(value);
  if (email) next.email = email;

  if (step === "name") {
    const name = extractName(value);
    if (name) next.name = name;
  } else if (step === "email") {
    if (email && isValidEmail(email)) next.email = email;
    else if (isValidEmail(value)) next.email = value.trim();
  }

  // One-shot: "John Smith john@x.com" on either step
  if (!next.name) {
    const maybeName = extractName(value);
    if (maybeName) next.name = maybeName;
  }

  return next;
}

/** Build EmailJS message = project summary from chat (no separate project field). */
export function buildLeadEmailBody(lead, chatMessages) {
  const transcript = (chatMessages || [])
    .filter((m) => m.role === "user" || m.role === "assistant")
    .slice(-16)
    .map((m) => `${m.role === "user" ? "Visitor" : "Assistant"}: ${m.content}`)
    .join("\n\n");

  return `New lead from Hiren’s AI chat

Name: ${lead.name}
Email: ${lead.email}

Project / conversation summary:
${transcript || "(No prior chat — visitor shared contact only.)"}`;
}
